import styles from "./styles.module.scss"
import { Room as IRoom, paymentRequestStatus } from "@cruisen/react-shared"
import { observer } from "mobx-react-lite"
import { useStores } from "../../../models/helpers/useStores"
import FileOutline from "~icons/eva/file-outline"
import PersonAddOutline from "~icons/eva/person-add-outline"
import FileAddOutline from "~icons/eva/file-add-outline"
import PlusOutline from "~icons/eva/plus-outline"
import { useMemo } from "react"
import { makeAutoObservable, toJS } from "mobx"
import { IProfile, PaymentRequestStatusEnum } from "@cruisen/contract"
import { ModalWrapper } from "../../templates/ModalWrapper"
import { BookingModal } from "../../organisms/BookingModal"
import { ImagePreviewModal } from "../../modals/ImagePreviewModal"
import { DocumentPreviewModal } from "../../modals/DocumentPreviewModal"
import { Tooltip } from "../../atoms/Tooltip"
import { AddGuestForm } from "./modals/AddGuestForm"
import { Popover } from "../../atoms/Popover"
import classNames from "classnames"
import { PaymentRequestModal } from "../../organisms/PaymentRequestModal"
import { getCardInfo, getPaymentRequestList } from "./utils"
import { TripRoomGuestCard, TripRoomGuestCardItem } from "../../cards/TripRoomGuestCard"
import { useDrop } from "react-dnd"
import {
  addGuestToTripRoom,
  deleteTripRoom,
  moveGuestToTripRoom,
  removeGuestFromTripRoom,
} from "../../../services/firebase-functions/rooms"
import { useGuestActions } from "../../../hooks/useGuestActions"
import { GuestDetailsModal } from "../../modals/GuestDetailsModal"
import { OptionPopover } from "../../atoms/OptionPopover"
import Trash2Outline from "~icons/eva/trash-2-outline"
import FileTextOutline from "~icons/eva/file-text-outline"
import LinedFolderIcon from "../../assets/icons/LinedFolderIcon"
import { ConfirmationModal } from "../../modals/ConfirmationModal"
import { useMutation } from "@tanstack/react-query"

type AvailableModals =
  | "addGuest"
  | "deleteRoom"
  | "bookingModal"
  | "bookingConfirmationFile"
  | "noTripSearchResultTooltip"
  | "noTripSearchResultQuoteTooltip"
  | "paymentRequestModal"

export type AvailableGuestActions = "guestDetailsModal" | "deleteGuest"

type AvailableRoomActions = "deleteRoom"

class RoomStore {
  visibleModal: null | AvailableModals
  guestModal: null | AvailableGuestActions
  roomModal: null | AvailableRoomActions
  guestId: null | string
  roomId: null | string

  constructor() {
    this.visibleModal = null
    this.guestId = null
    this.guestModal = null
    this.roomModal = null
    this.roomId = null

    makeAutoObservable(this)
  }

  setRoomModal(modal: null | AvailableRoomActions) {
    this.roomModal = modal
  }

  setRoomId(id: string | null) {
    this.roomId = id
  }

  toggleRoomId(id: string | null) {
    if (this.roomId === id) {
      this.roomId = null
      return
    }
    this.roomId = id
  }

  setGuestId(id: string | null) {
    this.guestId = id
  }

  setGuestModal(modal: null | AvailableGuestActions) {
    this.guestModal = modal
  }

  toggleGuestId(id: string) {
    if (this.guestId === id) {
      this.guestId = null
      return
    }
    this.guestId = id
  }

  setModal(modal: null | AvailableModals) {
    this.visibleModal = modal
  }

  toggleModal(modal: null | AvailableModals) {
    if (this.visibleModal === modal) {
      this.visibleModal = null
      return
    }
    this.visibleModal = modal
  }
}

export const Room = observer(
  ({
    roomData: roomDataMobx,
    testID,
    tripRoomProfiles,
    showDeleteRoom,
    setRecentlyDroppedGuests,
    isRecentlyDroppedGuest,
  }: {
    roomData: IRoom
    testID: string
    tripRoomProfiles?: (IProfile & { isDataFilled: boolean })[]
    showDeleteRoom: boolean
    setRecentlyDroppedGuests: (guestId: string) => void
    isRecentlyDroppedGuest: (guestId: string) => boolean
  }) => {
    const rootStore = useStores()
    const store = useMemo(() => new RoomStore(), [])
    const { moveGuestToRoom, addGuestToRoom } = useGuestActions()
    const roomData = toJS(roomDataMobx)
    const cardInfo = getCardInfo(roomData.bookingDetails)
    const isRoomBooked = roomData.bookingDetails !== null
    const isDemoTrip = rootStore.activeTrip?.tags.includes("demo")

    const showRoomActions = roomData.guests.length > 0

    const isProfileIncomplete = (guestProfileId: number) => {
      if (isDemoTrip) return false

      return (
        tripRoomProfiles &&
        !tripRoomProfiles.find((profile) => profile.id === guestProfileId)?.isDataFilled
      )
    }
    const paymentRequestList = getPaymentRequestList(roomData)

    const activePaymentRequest =
      paymentRequestList.find(
        (paymentRequest) =>
          paymentRequest.status !== PaymentRequestStatusEnum.CancelledByAgent &&
          paymentRequest.status !== PaymentRequestStatusEnum.RejectedByUser,
      ) || null

    const hasApprovedPaymentRequest = paymentRequestList.some(
      (paymentRequest) => paymentRequest.status === PaymentRequestStatusEnum.Approved,
    )

    const openPaymentRequestDetails = () => {
      rootStore.setProp("cruiseDetailsPlaceholderData", null)

      if (!activePaymentRequest || rootStore.paymentRequest === activePaymentRequest?.id) {
        rootStore.setProp("paymentRequest", null)
      } else {
        rootStore.setProp("paymentRequest", activePaymentRequest?.id)
      }
    }

    const [{ isOver, canDrop }, drop] = useDrop(() => ({
      accept: "tripRoomGuestCard",
      drop: async (item: TripRoomGuestCardItem) => {
        await handleDrop(item)
      },
      canDrop: (item) => {
        return !(item.roomId === roomData.id || activePaymentRequest || isRoomBooked)
      },
      collect: (monitor) => ({
        isOver: Boolean(monitor.isOver()),
        canDrop: Boolean(monitor.canDrop()),
      }),
    }))

    const handleDrop = async (item: TripRoomGuestCardItem) => {
      setRecentlyDroppedGuests(item.guest?.userId || item.guest.id)

      if (!item.roomId) {
        addGuestToRoom(item.guest, roomData.id)

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, ...guestData } = item.guest
        await addGuestToTripRoom({
          tripId: rootStore.activeTrip!.id,
          roomId: roomData.id,
          guestData,
          skipProfileCreation: true,
        })
      } else {
        moveGuestToRoom(item.guest, item.roomId, roomData.id)
        await moveGuestToTripRoom({
          tripId: item.tripId,
          roomId: item.roomId,
          newRoomId: roomData.id,
          guestData: item.guest,
        })
      }
    }

    const currentGuest = roomData.guests.find((guest) => guest.id === store.guestId) || null

    const { mutateAsync: mutateRemoveRoom, isPending: isRemovingRoom } = useMutation({
      mutationFn: () =>
        deleteTripRoom({
          tripId: rootStore.activeTrip!.id,
          roomId: roomData.id,
        }),
      onSuccess: () => {
        store.setRoomModal(null)
      },
    })

    const roomOptions = [
      {
        label: "Room details",
        onClick: () => store.setModal("bookingModal"),
        icon: <FileTextOutline />,
        isHidden: !roomData.guests?.length,
      },
      {
        label: "New quote",
        onClick: () => {
          if (!rootStore.activeTrip.chosenTripSearchResultId) {
            if (store.visibleModal === "noTripSearchResultQuoteTooltip") return

            store.toggleModal("noTripSearchResultQuoteTooltip")
            setTimeout(() => {
              store.toggleModal(null)
            }, 3000)
            return
          }

          store.toggleModal("paymentRequestModal")
        },
        icon: <PlusOutline />,
        isHidden: !activePaymentRequest || isRoomBooked,
      },
      {
        label: "Quote details",
        onClick: openPaymentRequestDetails,
        icon: <LinedFolderIcon />,
        isHidden: !activePaymentRequest,
      },
      {
        label: "Delete room",
        onClick: () => store.setRoomModal("deleteRoom"),
        icon: <Trash2Outline />,
        isHidden: !(!isRoomBooked && !activePaymentRequest && showDeleteRoom),
        danger: true,
      },
    ]

    const { mutateAsync: mutateRemoveGuest, isPending: isRemovingGuest } = useMutation({
      mutationFn: () =>
        removeGuestFromTripRoom({
          tripId: rootStore.activeTrip!.id,
          roomId: roomData.id,
          guestId: currentGuest?.id,
        }),
      onSuccess: () => {
        store.setGuestModal(null)
        store.setGuestId(null)
      },
    })

    return (
      <>
        <div className={styles.roomCard} data-testid={testID} ref={drop}>
          <div className={styles.headerSection}>
            <div className={styles.roomTitleContainer}>
              <p className={styles.roomTitle}>{roomData.name}</p>
              {isRoomBooked ? (
                cardInfo ? (
                  <p
                    className={classNames(styles.cabinDetails, styles.cabinDetailsBooked)}
                    data-testid={`${testID}-cabin-details`}
                  >
                    {cardInfo.cabinLocation}
                    <br />
                    {cardInfo.cabinName}
                  </p>
                ) : (
                  <p
                    className={classNames(styles.cabinDetails, styles.cabinDetailsBooked)}
                    data-testid={`${testID}-generic-booked-room`}
                  >
                    {`Guaranteed ${roomData.bookingDetails?.cabinGrade} cabin`}
                  </p>
                )
              ) : activePaymentRequest ? (
                <p
                  className={classNames(
                    styles.cabinDetails,
                    paymentRequestStatus.get(activePaymentRequest.status)?.statusType === "success"
                      ? styles.cabinDetailsApproved
                      : styles.cabinDetailsPending,
                  )}
                  data-testid={`${testID}-room-status-container`}
                >
                  {paymentRequestStatus.get(activePaymentRequest.status)?.roomLabel}
                </p>
              ) : (
                <p className={styles.cabinDetails}>Open</p>
              )}
            </div>
            {showRoomActions || showDeleteRoom ? (
              <OptionPopover
                isOpen={
                  store.roomId === roomData.id &&
                  store.roomModal === null &&
                  store.visibleModal === null
                }
                onClose={(e) => {
                  const target = e?.target as HTMLElement
                  if (!target) return

                  const isInActionPopover = target?.closest("#room-action-popover") !== null
                  const isOptionClicked = (e?.target as { id?: string })?.id.includes("option-")

                  if (isOptionClicked || isInActionPopover) return

                  store.setRoomModal(null)
                  store.setRoomId(null)
                }}
                openPopover={() => {
                  store.toggleRoomId(roomData.id)
                  store.setRoomModal(null)
                }}
                options={roomOptions}
                testId={`${testID}-room-actions`}
                align="end"
                wrapperClassName={styles.roomActionsPopover}
              />
            ) : null}
            {store.visibleModal === "noTripSearchResultQuoteTooltip" ? (
              <Tooltip
                id={`noTripSearchResultQuoteTooltip-${testID}`}
                isOpen={store.visibleModal === "noTripSearchResultQuoteTooltip"}
                place="left"
              />
            ) : null}
          </div>
          <div className={styles.guestListContainer}>
            {roomData.guests.map((guest, index) => (
              <div
                key={guest.id}
                className={classNames(
                  styles.guestContainer,
                  isRecentlyDroppedGuest(guest.userId || guest.id) ? styles.guestAnimation : "",
                )}
              >
                <TripRoomGuestCard
                  guest={guest}
                  testID={`${testID}-guest-${index + 1}`}
                  isProfileIncomplete={isProfileIncomplete(guest.profileId)}
                  onGuestActionsClick={() => {
                    store.setGuestModal(null)
                    store.toggleGuestId(guest.id)
                  }}
                  activePaymentRequest={Boolean(activePaymentRequest)}
                  openGuestDetailsModal={() => store.setGuestModal("guestDetailsModal")}
                  openDeleteGuestModal={() => store.setGuestModal("deleteGuest")}
                  guestId={store.guestId}
                  guestModal={store.guestModal}
                  roomId={roomData.id}
                  tripId={rootStore.activeTrip!.id}
                  setGuestId={(id) => store.setGuestId(id)}
                  setGuestModal={(modal) => store.setGuestModal(modal)}
                />
              </div>
            ))}
          </div>
          {isOver && canDrop ? (
            <div className={styles.dropOverlay}>
              <PlusOutline className={styles.dropOverlayIcon} />
            </div>
          ) : null}
          {!isRoomBooked ? (
            <div className={styles.buttonContainer}>
              {!activePaymentRequest ? (
                <Popover
                  isOpen={store.visibleModal === "addGuest"}
                  onClickOutside={() => store.setModal(null)}
                  positions={["left"]}
                  content={
                    <AddGuestForm
                      closePopover={() => store.setModal(null)}
                      tripId={rootStore.activeTrip!.id}
                      roomId={roomData.id}
                      userPhoneNumber={rootStore.loggedInUser?.phoneNumber}
                    />
                  }
                >
                  <button
                    className={styles.submitButton}
                    onClick={() => store.toggleModal("addGuest")}
                    type="button"
                    data-testid={`${testID}-add-guest-button`}
                  >
                    <PersonAddOutline />
                    Add guest
                  </button>
                </Popover>
              ) : null}
              {roomData.guests.length > 0 && Boolean(activePaymentRequest) ? (
                <button
                  className={styles.submitButton}
                  type="button"
                  data-tooltip-id={`noTripSearchResultTooltip-${testID}`}
                  data-tooltip-content={
                    !rootStore.activeTrip.chosenTripSearchResultId
                      ? "Please select a cruise first"
                      : "User must approve quote before booking"
                  }
                  data-testid={`${testID}-book-room-button`}
                  onClick={() => {
                    if (
                      !rootStore.activeTrip.chosenTripSearchResultId ||
                      !hasApprovedPaymentRequest
                    ) {
                      if (store.visibleModal === "noTripSearchResultTooltip") return

                      store.toggleModal("noTripSearchResultTooltip")
                      setTimeout(() => {
                        store.toggleModal(null)
                      }, 3000)
                      return
                    }

                    store.toggleModal("bookingModal")
                    rootStore.setProp("activeFileDropZone", "bookingModal")
                  }}
                >
                  <FileAddOutline />
                  Book room
                </button>
              ) : null}
              {roomData.guests.length > 0 && !activePaymentRequest ? (
                <button
                  className={styles.submitButton}
                  type="button"
                  data-tooltip-id={`noTripSearchResultTooltip-${testID}`}
                  data-tooltip-content="Please select a cruise first"
                  data-testid={`${testID}-quote-room-button`}
                  onClick={() => {
                    if (!rootStore.activeTrip.chosenTripSearchResultId) {
                      if (store.visibleModal === "noTripSearchResultTooltip") return

                      store.toggleModal("noTripSearchResultTooltip")
                      setTimeout(() => {
                        store.toggleModal(null)
                      }, 3000)
                      return
                    }

                    store.toggleModal("paymentRequestModal")
                  }}
                >
                  <FileAddOutline />
                  Quote room
                </button>
              ) : null}
              {store.visibleModal === "noTripSearchResultTooltip" ? (
                <Tooltip
                  id={`noTripSearchResultTooltip-${testID}`}
                  isOpen={store.visibleModal === "noTripSearchResultTooltip"}
                  place="bottom"
                  testID={`${testID}-no-trip-search-result-tooltip`}
                />
              ) : null}
            </div>
          ) : null}
          {roomData?.bookingDetails?.confirmationFile ? (
            <>
              <button
                className={styles.bookingConfirmationFileContainer}
                type="button"
                data-testid={`${testID}-booking-confirmation-file`}
                onClick={() => {
                  store.setModal("bookingConfirmationFile")
                }}
              >
                <div className={styles.iconWrapper}>
                  <FileOutline className={styles.fileIcon} />
                </div>
                <p>Booking confirmation</p>
              </button>
              <>
                {store.visibleModal === "bookingConfirmationFile" ? (
                  <>
                    {roomData?.bookingDetails?.confirmationFile.type === "image" ? (
                      <ImagePreviewModal
                        files={[
                          {
                            fileName: "Booking confirmation",
                            fileUrl: roomData?.bookingDetails?.confirmationFile.url,
                          },
                        ]}
                        onClose={() => store.setModal(null)}
                      />
                    ) : (
                      <DocumentPreviewModal
                        fileUrl={roomData?.bookingDetails?.confirmationFile.url}
                        onClose={() => store.setModal(null)}
                      />
                    )}
                  </>
                ) : null}
              </>
            </>
          ) : null}
        </div>
        {store.visibleModal === "bookingModal" ? (
          <ModalWrapper>
            <BookingModal
              closeModal={() => {
                store.setModal(null)
                rootStore.setProp("activeFileDropZone", null)
              }}
              initialRoomId={roomData.id}
            />
          </ModalWrapper>
        ) : null}
        {store.visibleModal === "paymentRequestModal" ? (
          <ModalWrapper>
            <PaymentRequestModal
              closeModal={() => store.setModal(null)}
              initialRoomId={roomData.id}
            />
          </ModalWrapper>
        ) : null}
        {store.guestModal === "guestDetailsModal" && currentGuest ? (
          <ModalWrapper>
            <GuestDetailsModal
              closeModal={() => {
                store.setGuestModal(null)
                store.setGuestId(null)
              }}
              guest={currentGuest}
              profile={
                currentGuest?.profileId
                  ? tripRoomProfiles?.find((profile) => profile.id === currentGuest.profileId)
                  : undefined
              }
              roomId={roomData.id}
            />
          </ModalWrapper>
        ) : null}
        {store.roomModal === "deleteRoom" ? (
          <ConfirmationModal
            title="Delete room"
            description={`Are you sure you want to remove ${roomData.name}?`}
            onConfirm={mutateRemoveRoom}
            closeModal={() => store.setRoomModal(null)}
            isLoading={isRemovingRoom}
            confirmButtonText={isRemovingRoom ? "Removing..." : "Remove"}
            testId={`${testID}-delete-room-confirmation`}
          />
        ) : null}
        {store.guestModal === "deleteGuest" && currentGuest ? (
          <ConfirmationModal
            title="Delete guest"
            description={`Are you sure you want to remove ${currentGuest.firstName} ${currentGuest.lastName}?`}
            onConfirm={mutateRemoveGuest}
            closeModal={() => {
              store.setGuestModal(null)
              store.setGuestId(null)
            }}
            isLoading={isRemovingGuest}
            confirmButtonText={isRemovingGuest ? "Removing..." : "Remove"}
            testId={`${testID}-delete-guest-confirmation`}
          />
        ) : null}
      </>
    )
  },
)
